import { useMemo, useRef, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { AddableCourse, CourseOption } from "../../types/course.response";
import { getCourseKey } from "../../utils/courseUtils";
import Plus from "../icons/Plus";
import Loading from "../loading/Loading";
import MissingCourseModal from "../shared/MissingCourseModal";
import { searchApi } from "../../services/api/search.api";

interface SidebarAddCourseProps {
    allCourses: { course: AddableCourse; isApplied: boolean }[];
    onAdd: (course: AddableCourse) => void | Promise<void>;
    label?: string;
    nodeId?: string | null;
    dropId?: string;
    isBroad?: boolean;
}

export default function SidebarAddCourse({
    allCourses,
    onAdd,
    label = "Add completed course...",
    nodeId = null,
    dropId = `sidebar-add-slot-${nodeId ?? "unassigned"}-${label}`,
    isBroad = false,
}: SidebarAddCourseProps) {
    const [isAdding, setIsAdding] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [pendingCourseKey, setPendingCourseKey] = useState<string | null>(null);
    const [isMissingCourseModalOpen, setIsMissingCourseModalOpen] = useState(false);
    const [broadResults, setBroadResults] = useState<CourseOption[]>([]);
    const [isBroadLoading, setIsBroadLoading] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const isDroppable = nodeId !== null && !isAdding;
    const { setNodeRef, isOver } = useDroppable({
        id: dropId,
        data: { type: "empty-slot", nodeId },
        disabled: !isDroppable,
    });

    const filteredCourses = useMemo(() => {
        if (isBroad) return [];
        if (!searchQuery.trim()) return allCourses;
        const q = searchQuery.toLowerCase().replace(/\s+/g, "");
        return allCourses.filter(({ course }) => {
            const code = `${course.courseInfo.subject}${course.courseInfo.number}`.toLowerCase();
            const titleShort = course.courseInfo.titleShort?.toLowerCase() ?? "";
            const titleLong = course.courseInfo.titleLong?.toLowerCase() ?? "";
            return code.includes(q) || titleShort.includes(q) || titleLong.includes(q);
        });
    }, [allCourses, searchQuery, isBroad]);

    const handleBroadSearchChange = (query: string) => {
        setSearchQuery(query);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query.trim() || query.trim().length < 2) {
            setBroadResults([]);
            setIsBroadLoading(false);
            return;
        }
        setIsBroadLoading(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const results = await searchApi.searchCourses(query);
                setBroadResults(results);
            } catch {
                setBroadResults([]);
            } finally {
                setIsBroadLoading(false);
            }
        }, 200);
    };

    const handleClickAdd = async (course: AddableCourse) => {
        const key = getCourseKey(course);
        setPendingCourseKey(key);
        try {
            await onAdd(course);
        } finally {
            setPendingCourseKey(null);
            setSearchQuery("");
            setIsSearchFocused(false);
            setIsAdding(false);
            setBroadResults([]);
        }
    };

    const handleStartAdding = () => {
        setIsAdding(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSearchBlur = () => {
        setIsSearchFocused(false);
        if (!searchQuery.trim()) {
            setIsAdding(false);
        }
    };

    const handleOpenMissingCourseModal = () => {
        setSearchQuery("");
        setIsSearchFocused(false);
        setIsAdding(false);
        setBroadResults([]);
        setIsMissingCourseModalOpen(true);
    };

    return (
        <div className="relative">
            {isAdding ? (
                <div>
                    <div className="flex items-center gap-2 px-2 py-1.5">
                        <Plus className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                        <div className="relative flex-1">
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Search courses..."
                                value={searchQuery}
                                onChange={(e) => isBroad ? handleBroadSearchChange(e.target.value) : setSearchQuery(e.target.value)}
                                onFocus={() => setIsSearchFocused(true)}
                                onBlur={handleSearchBlur}
                                className="w-full bg-transparent text-sm text-gray-600 placeholder-gray-400 focus:outline-none border-b border-gray-200 focus:border-blue-500 pb-0.5 transition-colors"
                            />
                            {isBroad && isBroadLoading && (
                                <div className="absolute right-0 top-1/2 -translate-y-1/2">
                                    <Loading size="sm" />
                                </div>
                            )}
                        </div>
                    </div>

                    {isSearchFocused && (
                        <div
                            className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-52 overflow-y-auto"
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            {isBroad ? (
                                searchQuery.trim().length < 2 ? (
                                    <div className="py-3 text-center text-xs text-gray-400">
                                        Type to search all courses
                                    </div>
                                ) : broadResults.length > 0 ? (
                                    <div className="p-1">
                                        {broadResults.map((course) => {
                                            const key = getCourseKey(course);
                                            const isPending = pendingCourseKey === key;
                                            const isDisabled = isPending || pendingCourseKey !== null;
                                            return (
                                                <div
                                                    key={key}
                                                    className={`flex items-center py-1.5 px-2 rounded text-xs ${
                                                        isPending
                                                            ? "bg-blue-50 cursor-default"
                                                            : isDisabled
                                                            ? "opacity-50 cursor-default"
                                                            : "cursor-pointer hover:bg-blue-50"
                                                    }`}
                                                    onClick={() => !isDisabled && handleClickAdd(course)}
                                                >
                                                    <span className="font-mono text-gray-600 flex-shrink-0">
                                                        {course.courseInfo.subject} {course.courseInfo.number}
                                                    </span>
                                                    <span className="ml-2 text-gray-500 truncate flex-1">
                                                        {course.topic ? `${course.courseInfo.titleShort} - ${course.topic}` : course.courseInfo.titleShort}
                                                    </span>
                                                    {isPending && <Loading size="sm" className="ml-1 flex-shrink-0" />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    !isBroadLoading && (
                                        <div className="py-3 text-center text-xs text-gray-400">
                                            No courses found
                                        </div>
                                    )
                                )
                            ) : (
                                filteredCourses.length > 0 ? (
                                    <div className="p-1">
                                        {filteredCourses.map(({ course, isApplied }) => {
                                            const key = getCourseKey(course);
                                            const isPending = pendingCourseKey === key;
                                            const isDisabled = isApplied || isPending || pendingCourseKey !== null;
                                            return (
                                                <div
                                                    key={key}
                                                    className={`flex items-center py-1.5 px-2 rounded text-xs ${
                                                        isApplied
                                                            ? "opacity-40 cursor-default"
                                                            : isPending
                                                            ? "bg-blue-50 cursor-default"
                                                            : isDisabled
                                                            ? "opacity-50 cursor-default"
                                                            : "cursor-pointer hover:bg-blue-50"
                                                    }`}
                                                    onClick={() => !isDisabled && handleClickAdd(course)}
                                                >
                                                    <span className="font-mono text-gray-600 flex-shrink-0">
                                                        {course.courseInfo.subject} {course.courseInfo.number}
                                                    </span>
                                                    <span className="ml-2 text-gray-500 truncate flex-1">
                                                        {course.topic ? `${course.courseInfo.titleShort} - ${course.topic}` : course.courseInfo.titleShort}
                                                    </span>
                                                    {isPending && <Loading size="sm" className="ml-1 flex-shrink-0" />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="py-3 text-center text-xs text-gray-400">
                                        No courses found
                                    </div>
                                )
                            )}
                            {(!isBroad || searchQuery.trim().length >= 2) && (
                                <div className="border-t border-gray-100 p-1">
                                    <button
                                        type="button"
                                        onClick={handleOpenMissingCourseModal}
                                        className="w-full rounded px-2 py-1.5 text-left text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
                                    >
                                        My course not found
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <button
                    ref={setNodeRef}
                    onClick={handleStartAdding}
                    className={`flex items-center gap-2 px-2 py-1.5 w-full rounded-md text-sm transition-colors text-left ${
                        isDroppable && isOver
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    }`}
                >
                    <Plus className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{label}</span>
                </button>
            )}
            <MissingCourseModal
                isOpen={isMissingCourseModalOpen}
                onClose={() => setIsMissingCourseModalOpen(false)}
                onConfirm={onAdd}
            />
        </div>
    );
}
